import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'environments/environment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { sortBy } from 'lodash';
import ExternalLayer from 'models/external-layer/external-layer.model';
import ExternalFeature from 'models/external-feature/external-feature.model';
import ArcgisToken from 'models/arcgis-token/arcgis-token.model';
import User from 'models/user/user.model';
import GeoJSONPoint from 'models/geojson-point/geojson-point.model';
import GeoJSONGeometry from 'models/geojson-geometry/geojson-geometry.model';
import ProjectPermission from 'models/project-permission/project-permission.model';
import Ticket from 'models/ticket/ticket.model';
import { CreateTicketComponent } from 'modules/ticket/create-ticket/create-ticket.component';
import { ArcgisService } from 'services/arcgis/arcgis.service';
import { ArcgisMapService } from 'services/arcgis-map/arcgis-map.service';
import { UserService } from 'services/user/user.service';
import { ProjectService } from 'services/project/project.service';
import { TicketService } from 'services/ticket/ticket.service';

@Component({
  selector: 'app-arcgis-map',
  templateUrl: './arcgis-map.component.html',
  styleUrls: ['./arcgis-map.component.scss'],
})
export class ArcgisMapComponent implements OnInit {
  @ViewChild('map', { static: true }) private mapViewEl: ElementRef;
  @Input() pointsToShow: BehaviorSubject<GeoJSONPoint<{}>[]> = new BehaviorSubject([]);
  @Input() filteredPoints: BehaviorSubject<Ticket[]> = new BehaviorSubject([]);
  @Input() layerList: BehaviorSubject<ExternalLayer[]> = new BehaviorSubject([]);
  @Input() public activeLayerSelector = false;
  @Input() editorEnabled = true;

  public addTicketActive = false;
  public editTicketActive = false;
  public currentTicket: Ticket = null;
  public user: User = new User();
  public permissions: ProjectPermission = new ProjectPermission();
  public selectionEnabled = false;
  public arcgisToken: ArcgisToken = new ArcgisToken();
  private pointsToShowPoints = [];
  private selectedPoints = [];
  private editorWidget;
  private editorWidgetExpand = null;
  private feature = null;
  
  private userSubscription: Subscription;
  private permissionsSubscription: Subscription;
  private filteredSubscription: Subscription;
  private clickedSubscription: Subscription;
  private pointsToShowSubscription: Subscription;
  private layerListSubscription: Subscription;
  @Input() public refSubject = new BehaviorSubject<boolean>(false);
  
  view: any;
  map: any;
  
  constructor(
    public arcgisMapService: ArcgisMapService,
    public arcgisService: ArcgisService,
    public userService: UserService,
    public projectService: ProjectService,
    private dialog: MatDialog,
    private ticketService: TicketService,
  ) { }

  async ngOnInit() {
    await this.initializeMap();
    this.arcgisMapService.selectionEnabled.subscribe((v) => this.selectionEnabled = v || true);
    this.userSubscription = this.userService.getCurrentUser().subscribe((user) => this.user = user);
    this.permissionsSubscription = this.projectService.getSelectedPermissions().subscribe((perms) => this.permissions = perms);
  }

  async initializeMap() {
    const token: ArcgisToken = await this.arcgisService.getAccessToken();
    this.arcgisMapService.esri.identityManager.registerToken({
      server: environment.kapuUrl,
      token: token.token,
    });

    let basemap = new this.arcgisMapService.esri.baseMap({
      baseLayers: [
        new this.arcgisMapService.esri.tileLayer({
          url: 'https://kapu.budapestkozut.hu/arcgis/rest/services/terepi_adatgyujto/terepi_adatgyujto_alapterkep/MapServer',
          title: "orto",
        }),
      ],
      title: "basemap",
      id: "basemap"
    });

    const mapProperties = {
      basemap: basemap,
    };

    this.map = new this.arcgisMapService.esri.map(mapProperties);

    const mapViewProperties = {
      container: this.mapViewEl.nativeElement,
      center: ['238655.48643831725', '650997.7076326172'],
      zoom: 3,
      map: this.map,
      // spatialReference: {
      //   wkid: 23700,
      // },
    };

    this.view = new this.arcgisMapService.esri.mapView(mapViewProperties);
    await this.view.when();

    this.view.on('click', (event) => {
      const geoj = new GeoJSONPoint({
        geometry: new GeoJSONGeometry({
          coordinates: [event.mapPoint.x, event.mapPoint.y],
        }),
      });
      this.arcgisMapService.getClickedPosition().next(geoj);
      this.view.hitTest({
        x: event.x,
        y: event.y,
      }).then((response) => {
        this.handleClick(response.results, geoj);
      });
    });

    this.filteredSubscription = this.filteredPoints.subscribe((filteredPoints) => {
      this.selectedPoints = filteredPoints;
      if (filteredPoints[0]) {
        const graphic = new this.arcgisMapService.esri.graphic({
          geometry: {
            type: 'polyline',
            paths: filteredPoints.map((point) => point.position.geometry.coordinates),
            spatialReference: {
              wkid: 23700,
            },
          },
        });
        this.view.goTo({ target: graphic }, { duration: 1000, easing: 'ease' });
      }
      const p = filteredPoints.map((filtered) => this.isSame(filtered, this.pointsToShowPoints));
      this.arcgisMapService.showFiltered(p)
    })

    this.clickedSubscription= this.arcgisMapService.clickedFeature.subscribe(() => {
      const p = this.selectedPoints.map((filtered) => this.isSame(filtered, this.pointsToShowPoints, true));
      this.arcgisMapService.showFiltered(p);
    })

    this.pointsToShowSubscription = this.pointsToShow.subscribe((pointsToShow) => {
      this.pointsToShowPoints = pointsToShow;
      if (pointsToShow[0]) {
        const graphic = new this.arcgisMapService.esri.graphic({
          geometry: {
            type: 'polyline',
            paths: pointsToShow.map((point) => point.geometry.coordinates),
            spatialReference: {
              wkid: 23700,
            },
          },
        });
        this.view.goTo({ target: graphic }, { duration: 310, easing: 'ease' });
      }
      const p = this.selectedPoints.map((filtered) => this.isSame(filtered, pointsToShow));
      this.arcgisMapService.showFiltered(p);
    });

    if (this.layerList) {
      this.layerListSubscription = this.layerList.subscribe((layers) => this.handleNewLayerList(layers));
    }

    this.map.add(this.arcgisMapService.filteredLayer);


    this.editorWidget = new this.arcgisMapService.esri.editor({
      container: document.createElement('div'),
      view: this.view,
      allowedWorkflows: ['update', 'create'],
    });

    this.editorWidgetExpand = new this.arcgisMapService.esri.expand({
      expandIconClass: "esri-icon-edit",
      view: this.view,
      content: this.editorWidget.domNode,
    });

    const layerListWidget = new this.arcgisMapService.esri.layerList({
      container: document.createElement('div'),
      view: this.view,
    });

    const layerListWidgetExpand = new this.arcgisMapService.esri.expand({
      expandIconClass: "esri-icon-layer-list",
      view: this.view,
      content: layerListWidget.domNode,
    });

    if (this.editorEnabled) {
      this.view.ui.add(this.editorWidgetExpand, 'top-right');
    }

    this.view.ui.add(layerListWidgetExpand, 'bottom-right');

    return this.view;
  }

  checkEqualityByJson(feature, position: GeoJSONPoint<{}>) {
    return feature
      ? JSON.stringify(feature.position.geometry.coordinates) === JSON.stringify(position.geometry.coordinates)
      : false
  }

  refresh() {
    const curr = this.ticketService.getSelectedList().value.previous;
    this.ticketService.list(curr.project, curr.page, curr.limit, curr.user, curr.status, curr.interval, curr.category, curr.filter, curr.district, curr.searchables);
  }

  isSame(filtered: Ticket, a: GeoJSONPoint<{}>[], isTrue?) {
    if (this.checkEqualityByJson(this.arcgisMapService.clickedFeature.value, filtered.position)) {
      return new GeoJSONPoint<{ symbolType: string, ticket: Ticket }>({
        ...filtered.position,
        properties: {
          symbolType: 'clicked',
          ticket: filtered,
        },
      })
    }

    if (a.some((b) => JSON.stringify(b) === JSON.stringify(filtered.position))) {
      return new GeoJSONPoint<{ symbolType: string, ticket: Ticket }>({
        ...filtered.position,
        properties: {
          symbolType: 'selected',
          ticket: filtered,
        },
      })
    }

    return new GeoJSONPoint<{ symbolType: string, ticket: Ticket }>({
      ...filtered.position,
      properties: {
        symbolType: 'normal',
        ticket: filtered,
      },
    });
  };

  handleNewLayerList(layers: ExternalLayer[]) {
    layers = sortBy(layers, 'index');
    this.arcgisMapService.possibleAttributes = [];
    for (let i = 0; i < layers.length; i++) {
      let lyr;
      if (layers[i].type === 'Feature Layer') {
        lyr = new this.arcgisMapService.esri.featureLayer({
          url: layers[i].url,
          outFields: ['*'],
        });
        lyr['externalLayer'] = layers[i];
        this.map.add(lyr);
      } else {
        let url: string = layers[i].url;
        if (layers[i].url.indexOf('/MapServer') > -1) {
          url = layers[i].url.replace('/0', '');
        }
        lyr = new this.arcgisMapService.esri.mapImageLayer({
          url,
          outFields: ['*'],
        });
        lyr["externalLayer"] = layers[i];
        this.map.add(lyr);
      }
      lyr.when(() => this.arcgisMapService.possibleAttributes.push({ name: lyr.externalLayer.name, url: lyr.externalLayer.url, fields: lyr.fields }));
    }
  }
  
  addNewOrEdit(event: MouseEvent) {
    event.stopPropagation();
    if (this.createTicket()) {
      return;
    }
    if (this.toggleAddTicket()) {
      return;
    }
    this.editTicketActive = !this.editTicketActive;
  }

  createTicket(): boolean {
    if (this.arcgisMapService.clickedFeature.value && this.arcgisMapService.clickedFeature.value.objectId.toString().length > 0) {
      const ref = this.dialog.open(
        CreateTicketComponent,
        {
          data: {
            position: this.arcgisMapService.clickedFeature.value.position,
            project: this.projectService.getSelectedInstance().value,
            clicked: this.arcgisMapService.clickedFeature.value,
            feature: this.feature,
          },
        },
      );
      ref.afterClosed().subscribe((refresh?: boolean) => {
        if (refresh) {
          this.refSubject.next(true);
        }
      })
      return true;
    }
    return false;
  }

  toggleAddTicket(): boolean {
    if (this.arcgisMapService.clickedFeature.value === null) {
      this.addTicketActive = !this.addTicketActive;
      return true;
    }
    return false;
  }
  
  async handleClick(features: any[], position: GeoJSONPoint<any>) {
    if (features.length > 0) {
      this.feature = features[0]
      this.editorWidget.renderNow();
    } else {
      await this.editorWidget.cancelWorkflow();
    }

    if (this.handleAddTicket(position)) {
      return;
    }
    
    this.handleEditTicket(position);

    this.currentTicket = null;

    if(this.handleTicketSelection(features, position)) {
      return;
    }
    this.arcgisMapService.clickedFeature.next(null);
  }

  handleAddTicket(position: GeoJSONPoint<any>) {
    if (this.addTicketActive) {
      this.addTicketActive = false;
      const dialogRef = this.dialog.open(
        CreateTicketComponent,
        {
          data: {
            position,
            project: this.projectService.getSelectedInstance().value,
            clicked: this.arcgisMapService.clickedFeature.value,
          },
        },
      );
      dialogRef.afterClosed().subscribe((bool) => {
        if (bool) {
          this.refSubject.next(true);
        }
      });
      return true;
    }
    return false;
  }

  handleEditTicket(position: GeoJSONPoint<any>) {
    if (this.editTicketActive) {
      this.editTicketActive = false;
      this.ticketService.update(new Ticket({
        ...this.currentTicket,
        position
      })).then(() => this.refresh());
    }
  }

  handleTicketSelection(features: any[], position: GeoJSONPoint<any>) {
    if (features.length > 0 && features[0].graphic.attributes) {
      let ft: ExternalFeature = new ExternalFeature({
        other: features[0].graphic.attributes,
        objectId: "",
        externalLayer: "",
        position: {
          geometry: new GeoJSONGeometry({
            coordinates: position.geometry.coordinates,
          }),
        },
      });
      ft.objectId = features[0].graphic.attributes.objectid || features[0].graphic.attributes.objectId || features[0].graphic.attributes.OBJECTID || '';
      ft.externalLayer = features[0].graphic.layer.externalLayer;
      if (features[0].graphic.geometry.x) {
        ft.position = new GeoJSONPoint({
          geometry: new GeoJSONGeometry({
            coordinates: [features[0].graphic.geometry.x, features[0].graphic.geometry.y],
          }),
        });
      } else {
        ft.position = new GeoJSONPoint({
          geometry: new GeoJSONGeometry({
            coordinates: [features[0].graphic.geometry.extent.center.x, features[0].graphic.geometry.extent.center.y],
          }),
        });
      }
      if (features[0].graphic.attributes.ticket) {
        this.currentTicket = features[0].graphic.attributes.ticket;
        ft.ticketId = features[0].graphic.attributes.ticket.id;
        this.arcgisMapService.clickedFeature.next(ft);
        return true;
      }
      this.arcgisMapService.clickedFeature.next(ft);
      return true;
    }
    return false;
  }

  changeHiddenStatus(item: ExternalLayer) {
    for (let i = 0; i < this.map.allLayers._items.length; i++) {
      if (this.map.allLayers._items[i].externalLayer) {
        if (this.map.allLayers._items[i].externalLayer.url.indexOf(item.url) > -1) {
          this.map.allLayers._items[i].visible = !this.map.allLayers._items[i].visible;
        }
      }
    }
  }

  ngOnDestroy() {
    if (this.view) {
      this.view.container = null;
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.permissionsSubscription) {
      this.permissionsSubscription.unsubscribe();
    }
    if (this.filteredSubscription) {
      this.filteredSubscription.unsubscribe();
    }
    if (this.clickedSubscription) {
      this.clickedSubscription.unsubscribe();
    }
    if (this.pointsToShowSubscription) {
      this.pointsToShowSubscription.unsubscribe();
    }
    if (this.layerListSubscription) {
      this.layerListSubscription.unsubscribe();
    }
  }

}
